import PropTypes from 'prop-types'
import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import useMedia from 'components/custom-hooks/useMedia'
import FallBackModal, { FALLBACK_TYPE } from 'components/portal/fall-back-modal'
import { storeRecentViewProduct } from 'components/recently-view/utils'
import useRecommendation from 'components/shared/useEffect/useRecommendation'
import Wrapper from 'components/wrapper'
import { getUserId } from 'lib/utils/common/commonUtils'
import {
  segmentTrackPDPInteract,
  shouldShowVoucherAlert,
} from 'lib/utils/product'
import WishlistSnackBar from 'components/wishlist/wishlist-snackbar'
import ReviewApiHandler from 'lib/api/review'
import Router from 'next/router'
import SharePage from 'components/shared/share-page/SharePage'
import StateStorage from 'lib/state-storage'
import { segmentTrackAction } from 'lib/segment'
import { EVENT } from 'lib/segment/const'
import ProductCarouselImages from './carousel-images/ProductCarouselImages'
import useMobileGallery from './carousel-images/useMobileGallery'
import { PDP_INTERACT_ELEM_EVENT_NAME, PDP_WISHLIST_REF_TYPE } from './const'
import DesktopWrapper from './information/desktop-wrapper'
import ProductDescriptions from './information/descriptions'
import ProductQRCode from './interactions/product-qr-code'
import ProductPriceName from './information/price-name'
import ProductInteractions from './interactions'
import RelatedProductSection from './related-section/related-products'
import styles from './styles.scss'
import WaitlistSnackbar from './waitlist-snackbar/WaitlistSnackbar'
import ProductReviews from './reviews/product-reviews/ProductReviews'
import ProductStyleGallery from './style-gallery/product-style-gallery/ProductStyleGallery'
import { segmentPdpSeeReviewsClicked } from './reviews/pdp-reviews-segment/pdpReviewsSegment'
import ProductBanner from './product-banner/ProductBanner'
import AttributesSelector from './interactions/attributes-selector'
import pdpSessionEvent from './pdp-event-tracking/pdpEventTracking'
import MobileBuyingStyles from './mobile-buying-flow'
import OutfitShopping from './outfit-shopping'
import OutfitContext from './outfit-shopping/context'
import { trustFallback } from './desktop-trust/copy'

const ProductContainer = ({
  refId,
  refType,
  auth,
  internationalization: i18n,
  isCoachMarkClosed,
  isPhone,
  product,
  isVirtualTryOn,
}) => {
  const {
    category,
    attributes,
    id_product: productId,
    images,
    manufacturer,
    name: productNname,
    prices,
    video,
    coming_soon_text,
    is_coming_soon,
    virtual_try_on_enabled,
  } = product
  const [ratingResult, setProductRating] = useState()
  const productRating =
    ratingResult?.productId === productId ? ratingResult.rating : undefined
  const [isApiResError, setIsApiResError] = useState(false)
  const [keyIndex, setKeyIndex] = useState(1)
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)
  const [isWaitlistSnackBarOpen, setWaitlistSnackBarOpen] = useState(false)
  const [isDeleteWishlistProductTimeOut, setIsDeleteWishlistProductTimeOut] =
    useState(false)
  const phone = useMedia('(max-width: 767px)')
  const isMobileBuyingFlow = Boolean(isPhone || phone)
  const isMobileGallery = useMobileGallery(isPhone, phone)
  const [recommendedProducts] = useRecommendation(productId)
  const pmloVoucherFlagValue = 'B' // 'VoucherPDP-20220321'
  const pmloVoucherAllUserFlagValue = true // 'EC-VoucherPDP-20220610'
  const token = StateStorage.getAuthToken()

  const brandCategories = useMemo(() => {
    const { country, language } = i18n
    const { id_category, friendly_url } = manufacturer
    // Support only space transform to kebab
    const formatUrl = friendly_url?.replace(/ /g, '-').toLowerCase()

    return {
      href: `/category?id=${id_category}`,
      as: `/${country}/${language}/clothes/${formatUrl}`,
    }
  }, [i18n, manufacturer])

  const showClosableFallbackModal = () => {
    setKeyIndex(keyIndex + 1)
    setIsApiResError(true)
  }

  const handleClickReviews = () => {
    const pdpReviews = document.getElementById(
      `pdp-reviews-container-scroll-point`,
    )
    pdpReviews.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    })
    segmentPdpSeeReviewsClicked({
      id_product: productId,
    })
  }

  useEffect(() => {
    if (Router.asPath.includes('#pdp-reviews-container-scroll-point')) {
      const pdpReviews = document.getElementById(
        `pdp-reviews-container-scroll-point`,
      )
      if (pdpReviews) {
        pdpReviews.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      }
    }
  }, [])

  useEffect(() => {
    const userId = getUserId(auth).toString()
    storeRecentViewProduct(productId, userId, attributes)
  }, [attributes, auth, productId])

  const cancelDeleteWishlist = () => {
    setIsDeleteWishlistProductTimeOut(false)
    setIsSnackBarOpen(false)
  }

  useEffect(() => {
    let current = true
    const fetchProductRating = async () => {
      try {
        const result = await ReviewApiHandler.getAverageProductRating({
          id_product: productId,
        })
        if (result && current) {
          setProductRating({ productId, rating: result?.product_rating })
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error(error)
      }
    }
    if (token) {
      fetchProductRating()
    }
    return () => {
      current = false
    }
  }, [productId, token])

  useEffect(() => {
    const startTime = Date.now()

    const trackSession = () => {
      const duration = Date.now() - startTime
      pdpSessionEvent({
        id_product: productId,
        ref_id: refId,
        ref_type: refType,
        duration,
      })
    }

    window.addEventListener('beforeunload', trackSession)
    Router.events.on('routeChangeStart', trackSession)

    return () => {
      window.removeEventListener('beforeunload', trackSession)
      Router.events.off('routeChangeStart', trackSession)
    }
  }, [productId, refId, refType])

  const disableScroll = () => {
    document.body.style.overflow = 'hidden' // Disable scrolling
  }

  const enableScroll = () => {
    document.body.style.overflow = '' // Restore scrolling
  }

  const handleClickSwipe = () => {
    const pdpImagesScrollPoint = document.getElementById(
      'pdp__carousel-images-swipe-up-bar',
    )
    const yOffset = -47
    const yPosition =
      pdpImagesScrollPoint.getBoundingClientRect().top +
      window.scrollY +
      yOffset

    window.scrollTo({
      top: yPosition,
      behavior: 'smooth',
    })
  }

  let startY = 0
  let isSwipingUp = false
  let isSwipingDown = false

  const handleTouchStart = (event) => {
    event.preventDefault()
    event.stopPropagation()
    startY = event.touches[0].clientY
    isSwipingUp = false
    isSwipingDown = false
    disableScroll()
  }

  const handleTouchMove = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const currentY = event.touches[0].clientY
    const swipeThreshold = 5

    if (startY - currentY > swipeThreshold) {
      isSwipingUp = true
    } else if (currentY - startY > swipeThreshold) {
      isSwipingDown = true
    }
  }

  const handleTouchEnd = (event) => {
    event.preventDefault()
    event.stopPropagation()
    enableScroll()

    if (isSwipingUp) {
      handleClickSwipe()
    } else if (isSwipingDown) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const shareProductEvent = () => {
    const data = {
      ref_id: refId,
      ref_type: refType,
      id_product: productId,
      brand: manufacturer?.name,
      id_site_category: category?.id_category,
      site_category_name: category?.link_rewrite,
    }
    segmentTrackAction(EVENT.productShared, data)
  }

  return (
    <div
      className={clsx('pdp__container', {
        'pdp-mobile-buying-flow': isMobileBuyingFlow,
      })}
    >
      <style jsx>{styles}</style>
      {isMobileBuyingFlow && <MobileBuyingStyles />}
      <div className="pdp__content-container">
        <OutfitContext.Provider
          value={isMobileBuyingFlow ? recommendedProducts : null}
        >
          <ProductCarouselImages
            language={i18n.language}
            isOpenVirtualTryOn={isVirtualTryOn}
            virtual_try_on_enabled={
              virtual_try_on_enabled && !isMobileBuyingFlow
            }
            is_italist={product?.is_italist}
            id={productId}
            images={images}
            video={video}
            isPhone={isPhone}
            isShownShopTheLook={recommendedProducts?.length > 0}
            phone={phone}
            shopLookCtaEventTracking={() => {
              segmentTrackPDPInteract(
                product,
                '',
                PDP_INTERACT_ELEM_EVENT_NAME.impression,
                {
                  feedPos: 1,
                  refType: PDP_WISHLIST_REF_TYPE.related,
                  relatedProductList: recommendedProducts,
                },
              )
              segmentTrackPDPInteract(
                product,
                i18n.country,
                PDP_INTERACT_ELEM_EVENT_NAME.shopLook,
              )
            }}
          />
        </OutfitContext.Provider>
        {isPhone && !isMobileGallery && (
          <div
            role="presentation"
            onClick={handleClickSwipe}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onTouchStart={handleTouchStart}
            className="pdp__carousel-images-swipe-up-wrapper"
          >
            <div
              id="pdp__carousel-images-swipe-up-bar"
              className="pdp__carousel-images-swipe-up-bar"
            />
          </div>
        )}
        <Wrapper
          condition={!phone && !isPhone}
          wrapper={(children) => (
            <DesktopWrapper className="pdp__information-container">
              {children}
            </DesktopWrapper>
          )}
        >
          <div
            role={isMobileGallery ? undefined : 'presentation'}
            {...(isPhone &&
              !isMobileGallery && {
                onTouchEnd: handleTouchEnd,
                onTouchMove: handleTouchMove,
                onTouchStart: handleTouchStart,
              })}
            className={
              isMobileGallery
                ? undefined
                : 'pdp__carousel-images-swipe-up-wrapper'
            }
          >
            {isPhone && (
              <ProductBanner
                is_coming_soon={is_coming_soon}
                coming_soon_text={coming_soon_text}
                is_flash_sale={prices?.is_flash_sale}
                flash_sale_seconds_left={prices?.flash_sale_seconds_left}
              />
            )}
            <ProductPriceName
              language={i18n.language}
              prices={prices}
              isPhone={isPhone}
              name={productNname}
              productId={productId}
              brandURL={brandCategories}
              manufacturer={manufacturer}
              isComingSoon={is_coming_soon}
              comingSoonText={coming_soon_text}
              isShownVoucherAlert={shouldShowVoucherAlert(
                auth,
                pmloVoucherAllUserFlagValue,
                pmloVoucherFlagValue,
              )}
              isDeleteWishlistProductTimeOut={isDeleteWishlistProductTimeOut}
              product={product}
              refType="pdp"
              refId={refId}
              setIsSnackBarOpen={setIsSnackBarOpen}
              avg_overall_product_rating={
                productRating?.avg_overall_product_rating
              }
              onClickReview={handleClickReviews}
            />
            <AttributesSelector product={product} />
          </div>
          <ProductInteractions
            productRating={productRating}
            shareAction={
              <SharePage
                customEvent={shareProductEvent}
                sentenceCase
                label={trustFallback('SHARE', i18n.language)}
              />
            }
            isMobileBuyingFlow={isMobileBuyingFlow}
            isVirtualTryOn={isVirtualTryOn}
            product={product}
            setIsSnackBarOpen={setIsSnackBarOpen}
            isCoachMarkClosed={isCoachMarkClosed}
            refType={PDP_WISHLIST_REF_TYPE.product}
            refId={product?.id_product}
            showFallback={showClosableFallbackModal}
            setWaitlistSnackBarOpen={setWaitlistSnackBarOpen}
            isDeleteWishlistProductTimeOut={isDeleteWishlistProductTimeOut}
          />
          <ProductDescriptions
            compactServices
            product={product}
            clarifyDelivery
            shippingAccordionId={
              isMobileBuyingFlow ? `pdp-shipping-${productId}` : undefined
            }
          />
          {!isMobileBuyingFlow && <ProductQRCode product={product} />}
        </Wrapper>
      </div>
      {!isMobileBuyingFlow && (
        <div id="pdp-reviews-containers" data-cy="pdp-reviews-containers">
          <ProductStyleGallery productId={productId} />
        </div>
      )}
      <div id="pdp-reviews-container" data-cy="pdp-reviews-container">
        <div id="pdp-reviews-container-scroll-point" />
        <ProductReviews
          productId={productId}
          productAverageRatings={productRating}
          category_link_rewrite={product?.category?.link_rewrite}
          product_link_rewrite={`${product?.link_rewrite}.html`}
        />
      </div>
      {isMobileBuyingFlow && (
        <React.Fragment>
          <OutfitShopping
            productId={productId}
            products={recommendedProducts}
          />
          <div id="pdp-reviews-containers" data-cy="pdp-reviews-containers">
            <ProductStyleGallery productId={productId} />
          </div>
        </React.Fragment>
      )}
      <RelatedProductSection
        recommendedProducts={recommendedProducts}
        product={product}
      />
      {isApiResError && (
        <FallBackModal keyIndex={keyIndex} modalType={FALLBACK_TYPE.close} />
      )}
      <WishlistSnackBar
        isSnackBarOpen={isSnackBarOpen}
        undoDeleteWishlist={cancelDeleteWishlist}
        onCloseSnackBar={() => {
          setIsSnackBarOpen(false)
          setIsDeleteWishlistProductTimeOut(true)
        }}
      />
      <WaitlistSnackbar
        isSnackBarOpen={isWaitlistSnackBarOpen}
        onCloseSnackBar={() => {
          setWaitlistSnackBarOpen(false)
        }}
      />
    </div>
  )
}

ProductContainer.propTypes = {
  refId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refType: PropTypes.string,
  auth: PropTypes.shape({
    customerData: PropTypes.shape({
      is_returning_customer: PropTypes.bool,
    }),
    isGuestMode: PropTypes.bool,
    user: PropTypes.shape({
      id_customer: PropTypes.number,
    }),
  }).isRequired,
  product: PropTypes.shape({
    is_italist: PropTypes.bool,
    attributes: PropTypes.arrayOf(PropTypes.shape({})),
    id_product: PropTypes.number.isRequired,
    link_rewrite: PropTypes.string,
    prices: PropTypes.shape({
      discounted_price_formatted: PropTypes.string,
      original_price_formatted: PropTypes.string,
      is_flash_sale: PropTypes.bool,
      flash_sale_seconds_left: PropTypes.number,
    }),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        path: PropTypes.string.isRequired,
      }),
    ).isRequired,
    coming_soon_text: PropTypes.string,
    is_coming_soon: PropTypes.bool,
    video: PropTypes.shape({
      cover_image_path: PropTypes.string.isRequired,
      video_path: PropTypes.string.isRequired,
    }),
    manufacturer: PropTypes.shape({
      id_category: PropTypes.number,
      friendly_url: PropTypes.string,
      name: PropTypes.string,
    }),
    name: PropTypes.string,
    sku: PropTypes.string,
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        display_color: PropTypes.string,
      }),
    ),
    category: PropTypes.shape({
      id_category: PropTypes.number,
      link_rewrite: PropTypes.string,
    }),
    model: PropTypes.shape({
      height: PropTypes.number,
      size: PropTypes.string,
    }),
    description: PropTypes.string,
    materials: PropTypes.shape({
      fabrics: PropTypes.shape({
        fabrics: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
    sales_count: PropTypes.number,
    virtual_try_on_enabled: PropTypes.bool,
  }),
  isPhone: PropTypes.bool.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
  }).isRequired,
  isCoachMarkClosed: PropTypes.bool,
  isVirtualTryOn: PropTypes.bool,
}

ProductContainer.defaultProps = {
  refId: null,
  refType: null,
  isCoachMarkClosed: false,
  product: {},
  isVirtualTryOn: false,
}

export default connect((state) => ({
  auth: state.auth,
  customerData: state.auth.customerData,
  isPhone: state.device.isPhone,
  internationalization: state.internationalization,
}))(ProductContainer)
